import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cname from "./style.module.scss";
import { PipetteImage, AddButton, MinusButton } from "../MicroPipetteImages";
import { MICROPIPETTE_TYPE } from "@learningblocks/exp-constants/lib/Utilities";
import { VOLUME_UPDATE_INTERVAL } from "../MicroPipette.constants";
import { useTranslation } from "react-i18next";

const { P2, P20, P200, P1000 } = MICROPIPETTE_TYPE;

export function SectionRight(props) {
  const [top, mid, bottom] = splitVolumeValue(props.volume, props.type);
  const [increment, setIncrement] = useState(false);
  const [decrement, setDecrement] = useState(false);
  const timeoutRef = useRef();
  
  const showContaminationToast = async () => {
    const { SimManager } = await import("@learningblocks/simulation-engine");
    SimManager.getInstance().addToast(
      "Contamination!",
      "<b>To avoid contamination,</b> put the tip on after setting the volume. Remove the current micropipette tip, set the volume, and then attach a new tip.",
      "Contamination"
    );
  };

  useEffect(() => {
    if (increment) {
      incrementVolume(VOLUME_UPDATE_INTERVAL);
    } else if (decrement) {
      decrementVolume(VOLUME_UPDATE_INTERVAL);
    } else {
      clearTimeoutRef();
    }
  }, [increment, decrement]);

  const { t } = useTranslation();

  // Rest of the code...

  function incrementVolume(delay) {
    props.incrementVolume();
    delay = delay < 50 ? 50 : delay * 0.9;
    timeoutRef.current = setTimeout(() => {
      incrementVolume(delay);
    }, delay);
  }

  function decrementVolume(delay) {
    props.decrementVolume();
    delay = delay < 50 ? 50 : delay * 0.9;
    timeoutRef.current = setTimeout(() => {
      decrementVolume(delay);
    }, delay);
  }

  function clearTimeoutRef() {
    clearTimeout(timeoutRef.current);
  }

  return (
    <div className={cname.sectionRight}>
      <PipetteImage className={cname.pipette} type={props.type} />
      <MinusButton
        className={cname.minus}
        onMouseDown={() => {
          if (props.hasTip) {
            showContaminationToast();
          } else {
            setDecrement(true);
          }
        }}
        onMouseUp={() => {
          setDecrement(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (props.hasTip) {
              showContaminationToast();
            } else {
              setDecrement(true);
            }
          }
        }}
        onKeyUp={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setDecrement(false);
          }
        }}
        tabIndex={0}
        aria-label={t("Decrement.string")}
      />
      <AddButton
        className={cname.add}
        onMouseDown={() => {
          if (props.hasTip) {
            showContaminationToast();
          } else {
            setIncrement(true);
          }
        }}
        onMouseUp={() => {
          setIncrement(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (props.hasTip) {
              showContaminationToast();
            } else {
              setIncrement(true);
            }
          }
        }}
        onKeyUp={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIncrement(false);
          }
        }}
        tabIndex={0}
        aria-label={t("Increment.string")}
      />
      <div className={cname[props.type]}>
        <p className={cname.top}>{top}</p>
        <p className={cname.mid}>{mid}</p>
        <p className={cname.bottom}>{bottom}</p>
      </div>
    </div>
  );
}

SectionRight.propTypes = {
  /**
   * Current Volume value
   */
  volume: PropTypes.number,
  /**
   * Type of Micropipette [ P2, P20, P200, P1000]
   */
  type: PipetteImage.propTypes.type,
  /**
   * Function to toggle State of increment
   */
  toggleIncrement: PropTypes.func,
  /**
   * Function to toggle State of decrement
   */
  toggleDecrement: PropTypes.func,
};

SectionRight.defualtProps = {
  hasTip: false,
};

function splitVolumeValue(volume, type) {
  switch (type) {
    case P1000: {
      return [
        parseInt((volume / 1000) % 10),
        parseInt((volume / 100) % 10),
        parseInt((volume / 10) % 10),
      ];
    }
    case P200: {
      return [
        parseInt((volume / 100) % 10),
        parseInt((volume / 10) % 10),
        parseInt((volume / 1) % 10),
      ];
    }
    case P20:
      return [
        parseInt((volume / 10) % 10),
        parseInt((volume / 1) % 10),
        parseInt(Math.ceil(volume / 0.1) % 10),
      ];
    case P2:
      return [
        parseInt((volume / 1) % 10),
        parseInt((volume / 0.1) % 10),
        parseInt(Math.ceil(volume / 0.01) % 10),
        // (parseFloat(volume.toFixed(2)) / 0.01) % 10,
      ];
    default:
      return [0, 0, 0];
  }
}
